import {
  Button,
  Card,
  Divider,
  InlineGrid,
  Select,
  Text,
} from "@shopify/polaris";
import {
  contentLanguageOptions,
  primaryTargetCountryOption,
} from "../../utils/constants";
import { useCallback, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const normalizeCountryCode = (value) => {
  if (!value) return "";
  if (typeof value === "object") {
    return String(value.value || value.handle || value.code || "").toUpperCase();
  }
  return String(value).toUpperCase();
};

const normalizeLanguageCode = (value) => {
  if (!value) return "en";
  const locale = String(value).toLowerCase();
  return locale.split("-")[0] || "en";
};

const GMCSettings = () => {
  const [selectedTargetMarket, setSelectedTargetMarket] = useState("US");
  const [selectedContentLanguage, setSelectedContentLanguage] = useState("en");
  const [googleAccount, setGoogleAccount] = useState("");
  const [merchantId, setMerchantId] = useState("");
  const [merchantWebsite, setMerchantWebsite] = useState("");
  const [countryOptions, setCountryOptions] = useState(
    primaryTargetCountryOption,
  );
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();

  const urlParams = new URLSearchParams(location.search);
  const paramValue = urlParams.get("shop");

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        let targetMarket = "US";
        let contentLanguage = "en";

        const googleResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/google/auth/info?shop=${paramValue}`,
        );
        const googleData = await googleResponse.json();
        if (googleData.data) {
          setGoogleAccount(googleData.data.email || "");
          setMerchantId(
            googleData.data.id || googleData.data.accountId || "",
          );
          setMerchantWebsite(
            googleData.data.websiteUrl ||
              googleData.data.homePageUri ||
              googleData.data.homepageUri ||
              "",
          );
        }

        const storeLocaleResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/shopify/store-locales?shop=${paramValue}`,
        );
        const storeLocaleData = await storeLocaleResponse.json();
        if (storeLocaleData.data?.storeLocales?.primaryLocale?.locale) {
          contentLanguage = normalizeLanguageCode(
            storeLocaleData.data.storeLocales.primaryLocale.locale,
          );
          setSelectedContentLanguage(contentLanguage);
        }

        const marketsResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/shopify/active-markets?shop=${paramValue}`,
        );
        const marketsData = await marketsResponse.json();
        const marketsPayload = marketsData.data || {};
        const primaryMarket = marketsPayload.primaryMarket;
        const primaryCode = normalizeCountryCode(
          primaryMarket?.handle || primaryMarket?.code || primaryMarket?.value,
        );

        if (primaryCode && /^[A-Z]{2}$/.test(primaryCode)) {
          targetMarket = primaryCode;
          setCountryOptions((current) => {
            if (current.some((option) => option.value === primaryCode)) {
              return current;
            }
            return [
              {
                label: primaryMarket?.name || primaryCode,
                value: primaryCode,
              },
              ...current,
            ];
          });
        }

        const storeResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/shopify/store/settings?shop=${paramValue}`,
        );
        const storeData = await storeResponse.json();
        const finalSettings = storeData.data;
        if (finalSettings) {
          const savedCountry = normalizeCountryCode(
            finalSettings.primaryTargetCountry,
          );
          if (savedCountry) {
            targetMarket = savedCountry;
          }
          if (finalSettings.contentLanguage) {
            contentLanguage = normalizeLanguageCode(
              finalSettings.contentLanguage,
            );
          }
        }

        setSelectedTargetMarket(targetMarket);
        setSelectedContentLanguage(contentLanguage);

        if (targetMarket || contentLanguage) {
          await saveSettings({
            primaryTargetCountry: targetMarket,
            contentLanguage,
          });
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
      } finally {
        setLoading(false);
      }
    };

    if (paramValue) {
      fetchSettings();
    }
  }, [paramValue]);

  const handleTargetMarketChange = useCallback(
    (value) => setSelectedTargetMarket(normalizeCountryCode(value)),
    [],
  );
  const handleContentLanguageChange = useCallback(
    (value) => setSelectedContentLanguage(normalizeLanguageCode(value)),
    [],
  );

  const saveSettings = async (settings = false) => {
    if (!settings) {
      settings = {
        primaryTargetCountry: selectedTargetMarket,
        contentLanguage: selectedContentLanguage,
      };
    }
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/shopify/store/update_settings?shop=${paramValue}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ settings }),
        },
      );

      const result = await response.json();
      if (!response.ok) {
        console.error("Error updating settings:", result);
        alert("Failed to update settings!");
      }
    } catch (error) {
      console.error("Error:", error);
      alert("An error occurred while updating settings.");
    }
  };

  const changeAccRequest = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to reconnect Google Merchant Center? You need to go through the integration process again if you proceed further.",
    );
    if (!confirmed) return;

    try {
      await fetch(
        `${process.env.REACT_APP_API_URL}/api/google/auth/delete?shop=${paramValue}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      navigate(`/settings?shop=${paramValue}`);
    } catch (error) {
      console.error("Error deleting account:", error);
    }
  };

  return (
    <Card>
      <Text variant="headingMd" as="h6">
        Google Merchant Center Settings
      </Text>
      {loading ? (
        <Text as="p" tone="subdued">
          Loading Merchant Center details…
        </Text>
      ) : null}
      <InlineGrid gap="400" columns={["oneThird", "twoThirds"]}>
        <div
          style={{
            textAlign: "right",
            marginTop: "10px",
            display: "flex",
            flexDirection: "column",
            rowGap: "5px",
          }}
        >
          <Text variant="bodyMd" fontWeight="medium">
            Google Account
          </Text>
          <Text variant="bodyMd" fontWeight="medium">
            Merchant ID
          </Text>
          <Text variant="bodyMd" fontWeight="medium">
            Merchant Center Website
          </Text>
        </div>
        <div
          style={{
            marginTop: "10px",
            display: "flex",
            flexDirection: "column",
            rowGap: "5px",
          }}
        >
          <Text variant="bodyMd">
            {googleAccount || "Not available"}
            {googleAccount ? (
              <span
                onClick={changeAccRequest}
                style={{
                  color: "rgba(0, 91, 211, 1)",
                  cursor: "pointer",
                  marginLeft: "5px",
                }}
              >
                (Change Account)
              </span>
            ) : null}
          </Text>
          <Text variant="bodyMd">
            {merchantId || "Not selected"}
            <span
              onClick={() =>
                navigate(`/merchant-selection?shop=${paramValue}`, {
                  state: { merchantId: merchantId },
                })
              }
              style={{
                color: "rgba(0, 91, 211, 1)",
                cursor: "pointer",
                marginLeft: "5px",
              }}
            >
              ({merchantId ? "Change Merchant ID" : "Select Merchant ID"})
            </span>
          </Text>
          <Text variant="bodyMd" fontWeight="medium">
            {merchantWebsite || "Not available"}
          </Text>
        </div>
      </InlineGrid>
      <Divider />
      <InlineGrid gap="400" columns={2}>
        <div
          style={{
            textAlign: "right",
            marginTop: "10px",
            display: "flex",
            flexDirection: "column",
            rowGap: "20px",
          }}
        >
          <Text variant="bodyMd" fontWeight="medium">
            Primary Target Country
          </Text>
          <Text variant="bodyMd" fontWeight="medium">
            Content Language
          </Text>
        </div>
        <div
          style={{
            marginTop: "10px",
            display: "flex",
            flexDirection: "column",
            rowGap: "10px",
          }}
        >
          <Select
            options={countryOptions}
            onChange={handleTargetMarketChange}
            value={selectedTargetMarket}
          />
          <Select
            options={contentLanguageOptions}
            onChange={handleContentLanguageChange}
            value={selectedContentLanguage}
          />
        </div>
      </InlineGrid>
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          marginTop: "10px",
        }}
      >
        <Button variant="primary" onClick={() => saveSettings()}>
          Change Target Market
        </Button>
      </div>
    </Card>
  );
};

export default GMCSettings;
